import React, { useMemo } from "react";
import { Table, Card, Tooltip } from "antd";
import dayjs from "dayjs";

const buildProductTypeTableData = (aggEcomDataProductType, weekInfo) => {
  if (!aggEcomDataProductType || aggEcomDataProductType.length === 0) return [];

  const productTypes = [
    ...new Set(
      aggEcomDataProductType
        .filter((d) => d._id.category) // skip the total row with no category
        .map((d) => d._id.category)
    ),
  ];

  const totalPerWeek = {};
  aggEcomDataProductType.forEach((d) => {
    totalPerWeek[d._id.weekNo] = (totalPerWeek[d._id.weekNo] || 0) + d.totalQty;
  });

  const tableRows = productTypes.map((type) => {
    const row = { key: type, productType: type };
    weekInfo.forEach((w) => {
      const entry = aggEcomDataProductType.find(
        (d) => d._id.category === type && d._id.weekNo === w.weekNo
      );
      const qty = entry ? entry.totalQty : 0;
      const percent =
        totalPerWeek[w.weekNo] && qty > 0
          ? ((qty / totalPerWeek[w.weekNo]) * 100).toFixed(1) + "%"
          : "0.0%";
      row[`qty_${w.weekNo}`] = qty;
      row[`pct_${w.weekNo}`] = percent;
    });
    return row;
  });

  const grandTotal = { key: "Grand Total", productType: "Grand Total" };
  weekInfo.forEach((w) => {
    const qtySum = tableRows.reduce(
      (sum, row) => sum + (row[`qty_${w.weekNo}`] || 0),
      0
    );
    grandTotal[`qty_${w.weekNo}`] = qtySum || "";
    grandTotal[`pct_${w.weekNo}`] = qtySum > 0 ? "100%" : "";
  });
  tableRows.push(grandTotal);

  return tableRows;
};

const buildProductTypeColumns = (weekInfo, tableData) => {
  const maxPerWeek = {};
  weekInfo.forEach((w) => {
    maxPerWeek[w.weekNo] = Math.max(
      ...tableData
        .filter((row) => row.productType !== "Grand Total")
        .map((row) => row[`qty_${w.weekNo}`] || 0)
    );
  });

  return [
    {
      title: <b>PRODUCT TYPE</b>,
      dataIndex: "productType",
      key: "productType",
      fixed: "left",
      width: 210,
      render: (v, row) =>
        row.productType === "Grand Total" ? (
          <p style={{ color: "#215623", fontWeight: 700 }}>{v}</p>
        ) : (
          <span style={{ fontWeight: 400 }}>{v}</span>
        ),
    },
    ...weekInfo.map((w) => ({
      title: (
        <Tooltip title={`Week ${w.weekNo}`}>
          <span style={{ fontWeight: 600 }}>{w.weekLabel}</span>
        </Tooltip>
      ),
      children: [
        {
          title: "SALES QTY",
          dataIndex: `qty_${w.weekNo}`,
          key: `qty_${w.weekNo}`,
          align: "right",
          render: (v, row) => {
            if (row.productType === "Grand Total") {
              return (
                <b style={{ color: "#215623" }}>
                  {v ? v.toLocaleString("id-ID") : ""}
                </b>
              );
            }
            return v === maxPerWeek[w.weekNo] && v > 0 ? (
              <b style={{ color: "#1e88e5" }}>{v.toLocaleString("id-ID")}</b>
            ) : v > 0 ? (
              <span style={{ fontWeight: 600 }}>
                {v.toLocaleString("id-ID")}
              </span>
            ) : (
              <span style={{ color: "#ccc" }}>-</span>
            );
          },
        },
        {
          title: "QTY %",
          dataIndex: `pct_${w.weekNo}`,
          key: `pct_${w.weekNo}`,
          align: "right",
          render: (v, row) =>
            row.productType === "Grand Total" ? (
              <b style={{ color: "#215623" }}>{v}</b>
            ) : v === "0.0%" ? (
              <span style={{ color: "#ccc" }}>{v}</span>
            ) : (
              <span>{v}</span>
            ),
        },
      ],
    })),
  ];
};

export default function EcomAggProductTypeTable({ aggEcomDataProductType }) {
  const weekInfo = useMemo(() => {
    if (!aggEcomDataProductType || aggEcomDataProductType.length === 0)
      return [];
    const result = [];
    aggEcomDataProductType.forEach((d) => {
      if (!result.find((w) => w.weekNo === d._id.weekNo)) {
        const start = dayjs(d.weekStartDate).format("D MMM");
        const end = dayjs(d.weekEndDate).format("D MMM");
        result.push({
          weekNo: d._id.weekNo,
          weekLabel: `${start} - ${end}`,
        });
      }
    });
    result.sort((a, b) => a.weekNo - b.weekNo);
    return result;
  }, [aggEcomDataProductType]);

  const tableData = useMemo(
    () => buildProductTypeTableData(aggEcomDataProductType, weekInfo),
    [aggEcomDataProductType, weekInfo]
  );

  const columns = useMemo(
    () => buildProductTypeColumns(weekInfo, tableData),
    [weekInfo, tableData]
  );

  if (!aggEcomDataProductType || aggEcomDataProductType.length === 0)
    return <div>No data</div>;

  return (
    <Card
      title={
        <span style={{ fontWeight: 600 }}>Product Type Sales per Week</span>
      }
      style={{
        width: "100%",
        margin: "30px 0",
        boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
        borderRadius: 18,
      }}
      bodyStyle={{ padding: 0 }}
    >
      <Table
        columns={columns}
        dataSource={tableData}
        pagination={false}
        bordered
        size="middle"
        scroll={{ x: "max-content" }}
        style={{
          borderRadius: 16,
          background: "#fff",
        }}
        rowClassName={(row) =>
          row.productType === "Grand Total"
            ? "ant-table-row-grandtotal"
            : undefined
        }
      />
    </Card>
  );
}
